import Pagination from "ui/pagination";
import { useEffect, useState } from "react";
import Search from "ui/search";
import Table from "ui/customers/table";
import useSWR from "swr";
import { ITEMS_PER_PAGE } from "lib/constants";
import { useContext } from "react";
import { AuthContext } from "context/AuthContext";
import { fetchWithParams } from "lib/fetcher";
import {
  useLocation,
  useSearchParams,
  useParams,
  useNavigate,
  Outlet,
} from "react-router-dom";
import TopNav from "ui/dashboard/topnav";
import OrderList from "ui/license/order-list";
import { getCustomers } from "./service";
import { Tabs, Skeleton } from "antd";
import type { TabsProps } from "antd";
import { clsx } from "clsx";
export enum LicenseTab {
  Purchase = "purchase",
  AI = "ai",
  History = "history",
}

export default function Layout() {
  const navigate = useNavigate();
  const { tabname } = useParams();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query") || "";
  const currentPage = Number(searchParams.get("page")) || 1;
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [tabKey, setTabKey] = useState(tabname || "purchase");

  const numTotalCustomers = data?.page?.total;
  const numTotalPages = Math.ceil(Number(numTotalCustomers) / ITEMS_PER_PAGE);

  useEffect(() => {
    if (tabKey === LicenseTab.History) {
      return;
    }
    const offset = currentPage - 1;
    const params = `${"name"}=${query}&${"page.limit"}=${ITEMS_PER_PAGE}&${"page.offset"}=${offset}`;
    setIsLoading(true);
    getCustomers(params).then((res) => {
      setIsLoading(false);
      if (res.status !== 200) {
        console.error("Failed to fetch customers");
        return;
      }
      setData(res.data);
    });
  }, [tabKey, query, currentPage]);

  useEffect(() => {
    navigate(`/dashboard/license/${tabKey}`, { replace: true });
  }, [tabKey]);

  const items: TabsProps["items"] = [
    {
      key: LicenseTab.Purchase,
      label: "Purchase",
      children: (
        <>
          <div className="flex items-center justify-between gap-2 pt-4">
            <Search placeholder="Search customer name" />
          </div>
          {isLoading ? (
            <Skeleton className="mt-4" />
          ) : (
            <>
              <Table data={data} page="license" />
              <div className="mt-5 flex w-full justify-center">
                <Pagination totalPages={numTotalPages} />
              </div>
            </>
          )}
        </>
      ),
    },
    {
      key: LicenseTab.AI,
      label: "AI",
      children: (
        <>
          <div className="flex items-center justify-between gap-2 pt-4">
            <Search placeholder="Search customer name" />
          </div>
          {isLoading ? (
            <Skeleton className="mt-4" />
          ) : (
            <>
              <Table data={data} page="license-ai" />
              <div className="mt-5 flex w-full justify-center">
                <Pagination totalPages={numTotalPages} />
              </div>
            </>
          )}
        </>
      ),
    },
    {
      key: LicenseTab.History,
      label: "Order History",
      children: <OrderList />,
    },
  ];
  return (
    <>
      <TopNav title="Inventory" />
      <div className="flex h-full flex-col md:flex-row md:overflow-hidden bg-white rounded-3xl mr-6 mb-6">
        <div className="w-full flex-none md:w-96 border-r border-neutral-200 px-6 py-4 overflow-y-auto h-full">
          <Tabs
            defaultActiveKey={LicenseTab.Purchase}
            destroyInactiveTabPane={true}
            items={items}
            renderTabBar={() => (
              <div className="w-full flex items-center relative">
                <button
                  className={clsx(
                    "w-1/2 text-center h-12 leading-[48px] disabled:text-neutral-400 disabled:cursor-not-allowed",
                    tabKey === LicenseTab.Purchase
                      ? "border-b-2 border-neutral-800 font-semibold"
                      : "border-b border-neutral-200",
                  )}
                  onClick={() => setTabKey(LicenseTab.Purchase)}
                >
                  Purchase
                </button>
                <button
                  className={clsx(
                    "w-1/2 text-center h-12 leading-[48px]",
                    tabKey === LicenseTab.AI
                      ? "border-b-2 border-neutral-800 font-semibold"
                      : "border-b border-neutral-200",
                  )}
                  onClick={() => setTabKey(LicenseTab.AI)}
                >
                  AI
                </button>
                <button
                  className={clsx(
                    "w-1/2 text-center h-12 leading-[48px]",
                    tabKey === LicenseTab.History
                      ? "border-b-2 border-neutral-800 font-semibold"
                      : "border-b border-neutral-200",
                  )}
                  onClick={() => setTabKey(LicenseTab.History)}
                >
                  Order History
                </button>
              </div>
            )}
            className="tab_style"
            centered={true}
            indicator={{
              align: "center",
            }}
            activeKey={tabKey}
          />
        </div>
        <div className="flex-grow">
          <Outlet />
        </div>
      </div>
    </>
  );
}
